import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["block", "clearInvalidDateOnDatepickerOpen", "disabled", "disableDaysWhenIsBirthday", "disabledDays", "disableOpenDatepickerWhileInvalidDate", "invalid", "isBirthday", "label", "locale", "maxDate", "minDate", "multiple", "notAllowedDateMessage", "onBlur", "onChange", "placeholder", "readOnly", "selectable", "size", "theme", "tooltip", "value", "withNotAllowedMessage", "withoutDisabledDays", "withRange", "mobilePosition", "hideCloseButton", "headerMobile", "onConfirm", "confirmLabel", "direction", "possibleDirections", "classes", "mobileCalendar", "success", "inputProps", "isResettableRange", "isHideWhenOutOfBlock"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames/bind';
import { defaultPropObject } from "../constants";
import { useWindowSize } from "../hooks";
import { Calendar } from "../icons";
import { ClickOutside } from "../internal/ClickOutside";
import { FieldIcon, FieldInput, FieldRoot, useField } from "../internal/Field";
import { Portal } from "../internal/Portal";
import { checkIsDisabledDay, formatDate, getDisabledTimestamps, toDate as toDateType, validateDate } from "../utils/date";
import noop from "../utils/noop";
import { isString, toArray } from "../utils/type";
import Datepicker from '../Datepicker';
import ScrollableCalendar from './components/ScrollableCalendar';
import { locales, mobileCalendarTypesEnum, themes } from './constants';
import { getDefaultValueByType, parseValue, validate } from './helpers';
import styles from './DateField.css';
var cx = classNames.bind(styles);
var DATE_MASK = '99.99.9999';
var DATE_RANGE_MASK = "".concat(DATE_MASK, " - ").concat(DATE_MASK);
var DATE_PLACEHOLDER = 'дд.мм.гггг';
var DATE_RANGE_PLACEHOLDER = "".concat(DATE_PLACEHOLDER, " - ").concat(DATE_PLACEHOLDER);
var DATE_PLACEHOLDER_EN = 'dd.mm.yyyy';
var DATE_RANGE_PLACEHOLDER_EN = "".concat(DATE_PLACEHOLDER_EN, " - ").concat(DATE_PLACEHOLDER_EN);
var defaultPossibleDirections = ['bottom', 'top'];

var DateField = function DateField(_ref) {
  var block = _ref.block,
      _ref$clearInvalidDate = _ref.clearInvalidDateOnDatepickerOpen,
      clearInvalidDateOnDatepickerOpen = _ref$clearInvalidDate === void 0 ? true : _ref$clearInvalidDate,
      disabled = _ref.disabled,
      _ref$disableDaysWhenI = _ref.disableDaysWhenIsBirthday,
      disableDaysWhenIsBirthday = _ref$disableDaysWhenI === void 0 ? true : _ref$disableDaysWhenI,
      disabledDaysProp = _ref.disabledDays,
      disableOpenDatepickerWhileInvalidDate = _ref.disableOpenDatepickerWhileInvalidDate,
      invalid = _ref.invalid,
      isBirthday = _ref.isBirthday,
      label = _ref.label,
      _ref$locale = _ref.locale,
      locale = _ref$locale === void 0 ? 'ru' : _ref$locale,
      _ref$maxDate = _ref.maxDate,
      maxDate = _ref$maxDate === void 0 ? '01.01.2050' : _ref$maxDate,
      _ref$minDate = _ref.minDate,
      minDate = _ref$minDate === void 0 ? '01.01.1980' : _ref$minDate,
      multiple = _ref.multiple,
      _ref$notAllowedDateMe = _ref.notAllowedDateMessage,
      notAllowedDateMessage = _ref$notAllowedDateMe === void 0 ? 'not allowed date' : _ref$notAllowedDateMe,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      placeholder = _ref.placeholder,
      readOnly = _ref.readOnly,
      selectable = _ref.selectable,
      size = _ref.size,
      _ref$theme = _ref.theme,
      theme = _ref$theme === void 0 ? themes[0] : _ref$theme,
      tooltip = _ref.tooltip,
      value = _ref.value,
      withNotAllowedMessage = _ref.withNotAllowedMessage,
      withoutDisabledDays = _ref.withoutDisabledDays,
      withRange = _ref.withRange,
      _ref$mobilePosition = _ref.mobilePosition,
      mobilePosition = _ref$mobilePosition === void 0 ? 'bottom' : _ref$mobilePosition,
      hideCloseButton = _ref.hideCloseButton,
      _ref$headerMobile = _ref.headerMobile,
      headerMobile = _ref$headerMobile === void 0 ? 'close' : _ref$headerMobile,
      onConfirm = _ref.onConfirm,
      confirmLabel = _ref.confirmLabel,
      _ref$direction = _ref.direction,
      direction = _ref$direction === void 0 ? 'bottom' : _ref$direction,
      _ref$possibleDirectio = _ref.possibleDirections,
      possibleDirections = _ref$possibleDirectio === void 0 ? defaultPossibleDirections : _ref$possibleDirectio,
      _ref$classes = _ref.classes,
      classes = _ref$classes === void 0 ? defaultPropObject : _ref$classes,
      _ref$mobileCalendar = _ref.mobileCalendar,
      mobileCalendar = _ref$mobileCalendar === void 0 ? mobileCalendarTypesEnum.scrollable : _ref$mobileCalendar,
      success = _ref.success,
      inputProps = _ref.inputProps,
      isResettableRange = _ref.isResettableRange,
      isHideWhenOutOfBlock = _ref.isHideWhenOutOfBlock,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isDatepickerVisible = _useState2[0],
      setIsDatepickerVisible = _useState2[1];

  var _useState3 = useState(true),
      _useState4 = _slicedToArray(_useState3, 2),
      isAllowedDate = _useState4[0],
      setIsAllowedDate = _useState4[1];

  var textFieldRef = useRef(null);
  var iconRef = useRef(null);
  var rootRef = useRef(null);
  var yearSelectRef = useRef(null);
  var monthSelectRef = useRef(null);

  var _useField = useField({
    onBlur: onBlur
  }),
      focused = _useField.focused,
      handleBlurHook = _useField.handleBlur,
      isAutofill = _useField.isAutofill,
      handleFocusHook = _useField.handleFocus;

  var disabledDays = useMemo(function () {
    if (withoutDisabledDays) {
      return null;
    }

    return (disabledDaysProp || []).concat(isBirthday && disableDaysWhenIsBirthday ? [{
      after: formatDate(new Date())
    }] : []);
  }, [disableDaysWhenIsBirthday, disabledDaysProp, isBirthday, withoutDisabledDays]);
  var disabledDaysTimestampMap = useMemo(function () {
    return getDisabledTimestamps(disabledDays);
  }, [disabledDays]);
  var inputRef = useRef(null);
  var parsedValue = useMemo(function () {
    return parseValue(value, {
      withRange: withRange
    });
  }, [value, withRange]);

  var _useWindowSize = useWindowSize(),
      width = _useWindowSize.width;

  var isMobile = width <= 767;
  useEffect(function () {
    if (withRange) {
      var _toArray = toArray(value),
          _toArray2 = _slicedToArray(_toArray, 2),
          fromDate = _toArray2[0],
          toDate = _toArray2[1];

      if (!validateDate(fromDate) || checkIsDisabledDay(disabledDaysTimestampMap, toDateType(fromDate)) || !validateDate(toDate) || checkIsDisabledDay(disabledDaysTimestampMap, toDateType(toDate))) {
        setIsAllowedDate(false);
      } else if (!validateDate(fromDate) || !validateDate(toDate) || toDateType(fromDate) > toDateType(toDate)) {
        setIsAllowedDate(false);
      } else if (!isAllowedDate) {
        setIsAllowedDate(true);
      }
    } else if (!validateDate(value) || checkIsDisabledDay(disabledDaysTimestampMap, toDateType(value))) {
      setIsAllowedDate(false);
    } else if (!isAllowedDate) {
      setIsAllowedDate(true);
    }
  }, [disabledDaysTimestampMap, isAllowedDate, parsedValue, value, withRange]);

  var handleToggleDatepicker = function handleToggleDatepicker(e) {
    e === null || e === void 0 ? void 0 : e.preventDefault();

    if (disabled) {
      return;
    }

    if (!isDatepickerVisible && !isAllowedDate) {
      if (disableOpenDatepickerWhileInvalidDate) {
        return;
      }

      if (clearInvalidDateOnDatepickerOpen) {
        onChange(getDefaultValueByType({
          withRange: withRange,
          multiple: multiple
        }));
        setIsAllowedDate(true);
      }
    }

    setIsDatepickerVisible(function (prev) {
      return !prev;
    });
  };

  var handleChange = function handleChange(e) {
    var isValid = validate(e.target.value, {
      withRange: withRange,
      timestampMap: disabledDaysTimestampMap
    });

    if (withRange && isString(e.target.value)) {
      var _e$target$value$split = e.target.value.split(' '),
          _e$target$value$split2 = _slicedToArray(_e$target$value$split, 3),
          fromDate = _e$target$value$split2[0],
          toDate = _e$target$value$split2[2];

      onChange([fromDate, toDate], isValid);
      return;
    }

    onChange(e.target.value, isValid);
  };

  var handleBlur = useCallback(function (e) {
    var _rootRef$current;

    if (e && (_rootRef$current = rootRef.current) !== null && _rootRef$current !== void 0 && _rootRef$current.contains(e === null || e === void 0 ? void 0 : e.relatedTarget)) {
      return;
    } // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore


    handleBlurHook(e);
  }, [handleBlurHook]);
  var handleCloseDatepicker = useCallback(function () {
    setIsDatepickerVisible(false);
    handleBlur();
  }, [handleBlur]);

  var getTooltip = function getTooltip() {
    if (isDatepickerVisible) return undefined;

    if (tooltip) {
      return tooltip;
    }

    return withNotAllowedMessage && notAllowedDateMessage ? notAllowedDateMessage : undefined;
  };

  var getPlaceholder = function getPlaceholder() {
    if (placeholder) return placeholder;

    if (locale !== locales[1]) {
      return withRange ? DATE_RANGE_PLACEHOLDER_EN : DATE_PLACEHOLDER_EN;
    }

    return withRange ? DATE_RANGE_PLACEHOLDER : DATE_PLACEHOLDER;
  };

  var hasMobileCalendar = !isBirthday && mobileCalendar === mobileCalendarTypesEnum.scrollable;
  var DatepickerComponent = isMobile ? ScrollableCalendar : Datepicker;
  var displayPicker = isMobile ? hasMobileCalendar && isDatepickerVisible : isDatepickerVisible;
  var ignoreRefs = useMemo(function () {
    return [iconRef, yearSelectRef, monthSelectRef];
  }, []);
  return /*#__PURE__*/React.createElement("div", {
    className: cx('Wrapper', classes.root),
    ref: rootRef
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('TextField', {
      TextField_block: block
    }),
    ref: textFieldRef
  }, /*#__PURE__*/React.createElement(FieldRoot, _extends({}, rest, {
    autofill: isAutofill,
    disabled: disabled,
    focused: focused || isDatepickerVisible,
    invalid: invalid,
    success: success,
    readOnly: readOnly,
    size: size,
    theme: theme,
    tooltip: getTooltip()
  }), /*#__PURE__*/React.createElement(FieldInput, _extends({}, inputProps, {
    ref: inputRef,
    disabled: disabled,
    invalid: invalid,
    label: label,
    mask: withRange ? DATE_RANGE_MASK : DATE_MASK,
    onBlur: handleBlur,
    onFocus: handleFocusHook,
    onChange: handleChange,
    readOnly: readOnly,
    placeholder: getPlaceholder(),
    theme: theme,
    value: parsedValue
  })), (!isMobile || hasMobileCalendar) && /*#__PURE__*/React.createElement(FieldIcon, {
    ref: iconRef,
    disablePointerEvents: !isAllowedDate && disableOpenDatepickerWhileInvalidDate || disabled,
    onClick: handleToggleDatepicker,
    theme: theme,
    invalid: invalid
  }, /*#__PURE__*/React.createElement(Calendar, null)))), displayPicker && /*#__PURE__*/React.createElement(Portal, {
    anchorRef: textFieldRef,
    interactive: true,
    direction: direction,
    possibleDirections: possibleDirections // Костыль позволяющий пересчитать positions в Portal через изменение forceRecalculationPosition
    // при изменение mobilePosition на center https://kaiten.s7.ru/space/84/card/391897
    ,
    forceRecalculatePosition: mobilePosition === 'center',
    offset: 2,
    className: cx('datepicker_component', classes.popover),
    isHideWhenOutOfBlock: isHideWhenOutOfBlock
  }, /*#__PURE__*/React.createElement(ClickOutside, {
    onClickOutside: isMobile ? noop : handleCloseDatepicker,
    ignoreRefs: ignoreRefs
  }, /*#__PURE__*/React.createElement(DatepickerComponent, _extends({
    onClose: handleCloseDatepicker
  }, _objectSpread({
    disabledDays: disabledDays,
    locale: locale,
    maxDate: maxDate,
    minDate: minDate,
    onChange: onChange,
    multiple: multiple,
    readOnly: readOnly,
    selectable: selectable,
    theme: theme,
    value: parsedValue,
    withRange: withRange,
    isResettableRange: isResettableRange,
    monthSelectRef: monthSelectRef,
    yearSelectRef: yearSelectRef
  }, isMobile ? {
    label: label,
    isOpen: isDatepickerVisible,
    mobilePosition: mobilePosition,
    hideCloseButton: hideCloseButton,
    headerMobile: headerMobile,
    onConfirm: onConfirm,
    confirmLabel: confirmLabel
  } : {}))))));
};

export default DateField;